import currency from "currency.js";
import { AttributeTerm, Term } from "./types";
import { ATTRIBUTE_NAMES } from "./const";

export function deduplicateById<T extends { id: string }>(arr: T[]): T[] {
  return arr.reduce<T[]>((acc, next) => {
    if (!next?.id || acc.find((item) => item.id === next.id)) {
      return acc;
    }
    return [...acc, next];
  }, []);
}

export function formatOptions(
  options?: Record<string, { optionPrice?: number; optionName?: string }>
) {
  if (!options) return [];

  return Object.entries(options)
    .map(([id, value]) => ({ ...value, id }))
    .filter((option) => option.optionName);
}

export function stripHtmlTags(originalString?: string | null) {
  if (!originalString) return "";

  return originalString.replace(/(<([^>]+)>)/gi, "");
}

export function makeCSV(content: (string | number | null)[][]): string {
  let csv = "";
  content.forEach((value) => {
    value.forEach((item, i) => {
      let innerValue = item === null ? "" : item.toString();
      let result = innerValue.replace(/"/g, '""');
      if (result.search(/("|,|\n)/g) >= 0) {
        result = '"' + result + '"';
      }
      if (i > 0) {
        csv += ",";
      }
      csv += result;
    });
    csv += "\n";
  });
  return csv;
}

export function toSnakeCase(str: string) {
  return str
    .replace(/[A-Z]/g, (letter, index) => {
      return index === 0 ? letter.toLowerCase() : "_" + letter.toLowerCase();
    })
    .replace(/\s+/g, "_")
    .replace(/-/g, "_");
}

export function sortByOptionalDate(a?: string, b?: string) {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return new Date(b).getTime() - new Date(a).getTime();
}

export const parsePriceString = (price: string): number => {
  return currency(price).value;
};

export function parsePriceRangeString(priceRange?: string | null): {
  low: number | null;
  high: number | null;
} {
  if (!priceRange) return { low: null, high: null };
  const prices = priceRange.split(" - ");

  if (prices.length === 1) {
    const price = parsePriceString(prices[0]);
    return { low: price, high: price };
  }

  return {
    low: parsePriceString(prices[0]),
    high: parsePriceString(prices[1]),
  };
}

export function isWpGroupProduct(
  product:
    | Queries.WpExternalProduct
    | Queries.WpGroupProduct
    | Queries.WpSimpleProduct
    | Queries.WpVariableProduct
): product is Queries.WpGroupProduct {
  return (
    product !== null && typeof product === "object" && "products" in product
  );
}

export function isWpSimpleProduct(
  p: Queries.Maybe<Queries.WpProduct>
): p is Queries.WpSimpleProduct {
  return p !== null && "name" in p && typeof p === "object";
}

export function isValidVideo(video: {
  readonly id: string;
  readonly videoDetails: {
    readonly youtubeId: string | null;
    readonly name: string | null;
    readonly description: string | null;
    readonly uploadDate: string | null;
  } | null;
}): video is {
  readonly id: string;
  readonly videoDetails: {
    readonly youtubeId: string;
    readonly name: string;
    readonly description: string;
    readonly uploadDate: string;
  };
} {
  return (
    video !== null &&
    typeof video === "object" &&
    video.videoDetails !== null &&
    typeof video.videoDetails === "object" &&
    typeof video.videoDetails.youtubeId === "string" &&
    typeof video.videoDetails.name === "string" &&
    typeof video.videoDetails.description === "string" &&
    typeof video.videoDetails.uploadDate === "string"
  );
}

export function isAttributeTerm(
  term: Term | null
): term is AttributeTerm {
  return (
    term !== null &&
    typeof term === "object" &&
    term.name !== null &&
    term.taxonomyName !== null &&
    term.nodeType !== null &&
    ATTRIBUTE_NAMES.hasOwnProperty(term?.taxonomyName || '')
  );
}

export const usdFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2
});

export const normalizePath = (path: string) => path.replace(/\/+$/, "");

